import {
    MoorVar,
    buildWsAttach,
    bytesFromWebSocketMessage,
    decodeCredentialsUpdatedEvent,
    dispatchClientEvent,
    handleWebSocketControlFrame,
    parseWsNarrativeEventMessage,
    type SessionCredentials,
} from "@moor/web-sdk";

import { commandInput } from "./commandInput.svelte.ts";
import { editor } from "./editor.svelte.ts";
import { messages } from "./messages.svelte.ts";
import { renderContent, renderTraceback } from "./render.ts";
import { room } from "./room.svelte.ts";

const WS_KEEPALIVE_MARKER = 0x00;
const KEEPALIVE_INTERVAL_MS = 45_000;
const ARKHAM_NAMESPACE = "arkham";
const STATE_NAMESPACE = "state";
const SESSION_KEY = "arkham_session";

interface StoredSession {
    authToken: string;
    clientToken: string | null;
    clientId: string | null;
    playerName: string;
    host: string;
}

function escapeHtml(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function decodeVar(v: unknown): unknown {
    return (new MoorVar(v as never) as unknown as { toJS(): unknown }).toJS();
}

function decodeVarStr(v: unknown): string | null {
    return (new MoorVar(v as never) as unknown as { asString(): string | null }).asString();
}

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "error";

class ConnectionStore {
    status = $state<ConnectionStatus>("disconnected");
    error = $state<string | null>(null);
    playerName = $state<string>("");
    host = $state<string>("");
    authToken = $state<string | null>(null);

    private ws: WebSocket | null = null;
    private credentials: SessionCredentials | null = null;
    private keepaliveTimer: ReturnType<typeof setInterval> | null = null;
    private isReconnecting = false;

    async login(
        host: string,
        player: string,
        password: string,
        mode: "connect" | "create" = "connect",
    ): Promise<void> {
        this.status = "connecting";
        this.error = null;

        this.playerName = player;
        try {
            const body = new URLSearchParams({ player, password });
            const response = await fetch(`${host}/auth/${mode}`, {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: body.toString(),
            });

            if (!response.ok) {
                const msg = await response.text().catch(() => response.statusText);
                throw new Error(`Login rejected (${response.status}): ${msg}`);
            }

            const authToken = response.headers.get("X-Moor-Auth-Token");
            if (!authToken) throw new Error("Server returned no auth token");

            const clientToken = response.headers.get("X-Moor-Client-Token");
            const clientId = response.headers.get("X-Moor-Client-Id");

            this.credentials = { authToken, clientToken, clientId, isInitialAttach: true };
            this.host = host;
            this.authToken = authToken;
            this.saveSession(host, player, authToken, clientToken, clientId);
            this.openWebSocket(host);
        } catch (e) {
            this.status = "error";
            this.error = e instanceof Error ? e.message : String(e);
        }
    }

    private openWebSocket(host: string) {
        if (!this.credentials) return;

        const wsBase = host.replace(/^http/, "ws");
        const { wsUrl, protocols } = buildWsAttach(wsBase, {
            mode: "connect",
            credentials: this.credentials,
        });

        const ws = new WebSocket(wsUrl, protocols);
        ws.binaryType = "arraybuffer";
        this.ws = ws;

        ws.onopen = () => {
            this.isReconnecting = false;
            this.status = "connected";
            this.startKeepalive();
            ws.send("__get_world_info");
            ws.send("__refresh_client_character_sheet");
        };

        ws.onclose = () => {
            if (this.isReconnecting) {
                // Token was rejected or expired — drop the stored session
                this.clearSession();
                this.isReconnecting = false;
                this.credentials = null;
                this.error = null;
            }
            this.status = "disconnected";
            this.stopKeepalive();
            this.ws = null;
        };

        ws.onerror = () => {
            if (this.isReconnecting) {
                this.clearSession();
                this.isReconnecting = false;
            }
            this.status = "error";
            this.error = "WebSocket connection lost";
            this.stopKeepalive();
            this.ws = null;
        };

        ws.onmessage = (ev) => this.handleRawMessage(ev.data);
    }

    private handleRawMessage(data: unknown) {
        try {
        const bytes = bytesFromWebSocketMessage(data);
        if (!bytes) return;

        if (handleWebSocketControlFrame(bytes, (payload) => this.ws?.send(payload))) return;

        dispatchClientEvent(bytes, {
            onNarrativeEventMessage: (narrative) => {
                const parsed = parseWsNarrativeEventMessage(narrative, decodeVar, decodeVarStr);
                if (!parsed) return;

                switch (parsed.kind) {
                    case "data":
                        if (parsed.namespace === ARKHAM_NAMESPACE) {
                            this.handleArkhamEvent(parsed.eventKind, parsed.payload);
                        } else if (parsed.namespace === STATE_NAMESPACE) {
                            this.handleStateEvent(parsed.eventKind, parsed.payload);
                        }
                        break;

                    case "present":
                        editor.openFromPresentation(
                            parsed.presentData.id ?? "",
                            parsed.presentData.target ?? "",
                            parsed.presentData.content ?? "",
                            parsed.presentData.content_type ?? "text/plain",
                            parsed.presentData.attributes,
                        );
                        break;

                    case "unpresent":
                        editor.close();
                        break;

                    case "notify": {
                        const html = renderContent(parsed.content, parsed.contentType);
                        messages.push({ id: crypto.randomUUID(), kind: "notify", html, timestamp: Date.now() });
                        break;
                    }

                    case "traceback": {
                        const html = renderTraceback(parsed.tracebackText);
                        messages.push({ id: crypto.randomUUID(), kind: "traceback", html, timestamp: Date.now() });
                        break;
                    }
                }
            },

            onSystemMessageEvent: (sysMsg) => {
                const text = sysMsg.message() ?? "";
                if (text) {
                    messages.push({
                        id: crypto.randomUUID(),
                        kind: "system",
                        html: `<span class="system-msg">${text}</span>`,
                        timestamp: Date.now(),
                    });
                }
            },

            onIgnoredEvent: () => {},
            onUnknownEvent: () => {},
            onMalformedEvent: () => {},

            onCredentialsUpdatedEvent: (creds) => {
                const update = decodeCredentialsUpdatedEvent(creds);
                if (update && this.credentials) {
                    this.credentials = {
                        ...this.credentials,
                        clientId: update.clientId,
                        clientToken: update.clientToken,
                    };
                    const raw = localStorage.getItem(SESSION_KEY);
                    if (raw) {
                        try {
                            const session: StoredSession = JSON.parse(raw);
                            session.clientToken = update.clientToken;
                            session.clientId = update.clientId;
                            localStorage.setItem(SESSION_KEY, JSON.stringify(session));
                        } catch { /* ignore corrupt entry */ }
                    }
                }
            },
        });
        } catch (err) {
            console.error("[arkham] handleRawMessage error:", err, "data type:", typeof data);
        }
    }

    private handleStateEvent(kind: string, payload: unknown) {
        switch (kind) {
            case "room_snapshot":
                room.applyRoomEvent(payload);
                break;
        }
    }

    private handleArkhamEvent(kind: string, payload: unknown) {
        switch (kind) {
            case "room.enter":
            case "room.background":
                room.applyRoomEvent(payload);
                break;
            case "client.branding":
                room.applyBrandingEvent(payload);
                break;
            case "world.title":
                if (payload && typeof payload === "object") {
                    const p = payload as Record<string, unknown>;
                    if (typeof p.title === "string") room.worldTitle = p.title;
                }
                break;
            case "character.sheet":
                room.applyCharacterSheet(payload);
                break;

            case "effect.flicker": {
                const p = (payload ?? {}) as Record<string, unknown>;
                room.triggerFlicker(typeof p.duration === "number" ? p.duration : 600);
                break;
            }

            // MOO-side: a verb must call player:tell_client("arkham", "client.completions", ["word", ...])
            // in response to the special @client-complete <partial> command.
            case "client.completions": {
                const p = (payload ?? {}) as Record<string, unknown>;
                const words = Array.isArray(p.words)
                    ? p.words.filter((w): w is string => typeof w === "string")
                    : [];
                commandInput.applyCompletions(words);
                break;
            }
        }
    }

    send(text: string) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            messages.push({
                id: crypto.randomUUID(),
                kind: "echo",
                html: `<span class="cmd-echo">&gt; ${escapeHtml(text)}</span>`,
                timestamp: Date.now(),
            });
            this.ws.send(text);
        } else {
            messages.push({
                id: crypto.randomUUID(),
                kind: "system",
                html: `<span class="system-msg">Not connected — command not sent.</span>`,
                timestamp: Date.now(),
            });
        }
    }

    // Send a tab-completion request to the server.
    // Requires a MOO verb that calls :match_environment(partial) and dispatches
    // a "client.completions" arkham event with { words: [...] }.
    requestCompletion(partial: string) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(`__match_objects_for_client ${partial}`);
        }
    }

    disconnect() {
        this.clearSession();
        this.ws?.close();
        this.ws = null;
        this.credentials = null;
        this.host = "";
        this.authToken = null;
        messages.clear();
        room.reset();
        editor.close();
        this.status = "disconnected";
        this.stopKeepalive();
    }

    async tryAutoReconnect(): Promise<void> {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return;
        let session: StoredSession;
        try {
            session = JSON.parse(raw);
        } catch {
            localStorage.removeItem(SESSION_KEY);
            return;
        }
        this.isReconnecting = true;
        this.playerName = session.playerName;
        this.host = session.host;
        this.authToken = session.authToken;
        this.status = "connecting";
        this.credentials = {
            authToken: session.authToken,
            clientToken: session.clientToken,
            clientId: session.clientId,
            isInitialAttach: true,
        };
        this.openWebSocket(session.host);
    }

    private saveSession(host: string, playerName: string, authToken: string, clientToken: string | null, clientId: string | null) {
        const session: StoredSession = { authToken, clientToken, clientId, playerName, host };
        localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }

    private clearSession() {
        localStorage.removeItem(SESSION_KEY);
    }

    private startKeepalive() {
        this.keepaliveTimer = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(new Uint8Array([WS_KEEPALIVE_MARKER]));
            }
        }, KEEPALIVE_INTERVAL_MS);
    }

    private stopKeepalive() {
        if (this.keepaliveTimer) {
            clearInterval(this.keepaliveTimer);
            this.keepaliveTimer = null;
        }
    }
}

export const connection = new ConnectionStore();
